/**
 * Maps unit-06's `ClientContext` and `CaptureResult` onto the shapes the server's zod schema keeps.
 *
 * The two differ (`ts` vs `timestamp`, `path` vs `route`, an object `tab`...) and zod strips unknown
 * keys, so sending `ClientContext` raw would save a record with empty logs and no error. A test parses
 * this module's output with the handler's real `SaveFeedbackBody` so a rename on either side fails.
 * Note text and request bodies never enter the context (ADR-19).
 */
import { FEEDBACK_COMPUTED_STYLE_KEYS } from '@ic/platform-contract';
import type { CaptureResult, ClientContext } from '@/lib/feedback';
import type { SaveCapture, SaveClientContext } from './types';

/** Serialized `context` ceiling on the server (64 KB). */
export const MAX_CONTEXT_BYTES = 64 * 1024;

const cap = (value: string | null | undefined, max: number): string | undefined =>
  value === null || value === undefined ? undefined : value.slice(0, max);

const iso = (ts: number): string | undefined => {
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
};

export function toSaveContext(client: ClientContext): SaveClientContext {
  const breadcrumb = [client.breadcrumb?.desktop, client.breadcrumb?.tool]
    .filter((part): part is string => typeof part === 'string' && part.length > 0)
    .map((part) => part.slice(0, 200))
    .slice(0, 20);

  const mapped: SaveClientContext = {
    route: cap(client.route, 500),
    buildVersion: cap(client.build.version, 100),
    theme: cap(client.mode ? `${client.theme} / ${client.mode}` : client.theme, 100),
    environment: cap(client.environment, 100),
    tab: cap(client.tab?.title ?? client.tab?.id, 200),
    tool: client.tool ? { id: cap(client.tool.manifestId, 200), label: cap(client.tool.label, 200) } : undefined,
    breadcrumb: breadcrumb.length > 0 ? breadcrumb : undefined,
    consoleLog: client.consoleLog.slice(-60).map((e) => ({
      level: e.level.slice(0, 20),
      message: e.message.slice(0, 500),
      timestamp: iso(e.ts),
    })),
    apiLog: client.apiLog.slice(-20).map((e) => ({
      method: e.method.slice(0, 10),
      route: e.path.slice(0, 300),
      status: e.status,
      durationMs: Number.isFinite(e.ms) ? e.ms : undefined,
      correlationId: cap(e.correlationId, 100),
      timestamp: iso(e.ts),
    })),
  };

  if (new TextEncoder().encode(JSON.stringify({ client: mapped })).length > MAX_CONTEXT_BYTES) {
    delete mapped.consoleLog;
    if (new TextEncoder().encode(JSON.stringify({ client: mapped })).length > MAX_CONTEXT_BYTES) {
      delete mapped.apiLog;
    }
  }
  return mapped;
}

const ALLOWED_STYLE_KEYS: ReadonlySet<string> = new Set(FEEDBACK_COMPUTED_STYLE_KEYS);

export function toSaveCapture(capture: CaptureResult | null): SaveCapture | undefined {
  if (!capture || capture.mode === 'page') return undefined;
  const out: SaveCapture = { mode: capture.mode };
  if (capture.rect) out.rect = { ...capture.rect };
  if (capture.mode === 'element') {
    out.selector = cap(capture.selector, 500);
    out.testId = cap(capture.testId, 200);
    out.tag = cap(capture.tag, 200);
    out.role = cap(capture.role, 200);
    out.ariaLabel = cap(capture.ariaLabel, 200);
    out.text = cap(capture.text, 200);
    if (capture.computedStyles) {
      out.computedStyles = Object.fromEntries(
        Object.entries(capture.computedStyles)
          .filter(([key]) => ALLOWED_STYLE_KEYS.has(key))
          .map(([key, value]) => [key, value.slice(0, 200)]),
      );
    }
  }
  return out;
}
